import { apiFetch, markLoaded, renderError } from './helpers';
import type { OpenMeteoResponse } from './types';

function getWeatherDescription(code: number): { text: string; icon: string } {
    const map: Record<number, { text: string; icon: string }> = {
        0: { text: 'Clear sky', icon: '☀️' },
        1: { text: 'Mainly clear', icon: '🌤️' },
        2: { text: 'Partly cloudy', icon: '⛅' },
        3: { text: 'Overcast', icon: '☁️' },
        45: { text: 'Fog', icon: '🌫️' },
        48: { text: 'Rime fog', icon: '🌫️' },
        51: { text: 'Light drizzle', icon: '🌦️' },
        53: { text: 'Moderate drizzle', icon: '🌦️' },
        55: { text: 'Dense drizzle', icon: '🌧️' },
        56: { text: 'Freezing drizzle', icon: '🌧️' },
        57: { text: 'Heavy freezing drizzle', icon: '🌧️' },
        61: { text: 'Slight rain', icon: '🌧️' },
        63: { text: 'Moderate rain', icon: '🌧️' },
        65: { text: 'Heavy rain', icon: '🌧️' },
        66: { text: 'Freezing rain', icon: '🌧️' },
        67: { text: 'Heavy freezing rain', icon: '🌧️' },
        71: { text: 'Slight snow', icon: '🌨️' },
        73: { text: 'Moderate snow', icon: '🌨️' },
        75: { text: 'Heavy snow', icon: '❄️' },
        77: { text: 'Snow grains', icon: '❄️' },
        80: { text: 'Slight showers', icon: '🌦️' },
        81: { text: 'Moderate showers', icon: '🌧️' },
        82: { text: 'Violent showers', icon: '🌧️' },
        85: { text: 'Slight snow showers', icon: '🌨️' },
        86: { text: 'Heavy snow showers', icon: '❄️' },
        95: { text: 'Thunderstorm', icon: '⛈️' },
        96: { text: 'Thunderstorm with hail', icon: '⛈️' },
        99: { text: 'Thunderstorm with heavy hail', icon: '⛈️' },
    };
    return map[code] || { text: 'Unknown', icon: '🌡️' };
}

function getWindDirection(degrees: number): string {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
}

export async function fetchWeather(): Promise<void> {
    const container = document.getElementById('weather-data');
    if (!container) return;

    const lat = 60.2194;
    const lon = 24.8134;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,wind_direction_10m&hourly=precipitation,temperature_2m,weather_code&forecast_days=3&wind_speed_unit=ms&timezone=Europe%2FHelsinki`;

    try {
        const response = await apiFetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data: OpenMeteoResponse = await response.json();
        const c = data.current;
        const weather = getWeatherDescription(c.weather_code);
        const windDir = getWindDirection(c.wind_direction_10m);

        const finnishParts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
            timeZone: 'Europe/Helsinki',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(new Date()).map(part => [part.type, part.value]));
        const now = new Date(Date.UTC(
            Number(finnishParts.year), Number(finnishParts.month) - 1, Number(finnishParts.day),
            Number(finnishParts.hour), Number(finnishParts.minute), Number(finnishParts.second),
        ));
        const today = now.toLocaleDateString('fi-FI', { timeZone: 'UTC' });
        let rainForecastHtml = '';
        let sunForecastHtml = '';
        if (data.hourly?.time && data.hourly?.precipitation) {
            const times = data.hourly.time;
            const precip = data.hourly.precipitation;
            const codes = data.hourly.weather_code!;
            let nextRainIndex = -1;
            for (let i = 0; i < times.length; i++) {
                const forecastTime = new Date(`${times[i]}Z`);
                if (forecastTime > now && precip[i] > 0) {
                    nextRainIndex = i;
                    break;
                }
            }
            if (nextRainIndex >= 0) {
                const rainTime = new Date(`${times[nextRainIndex]}Z`);
                const diffMs = rainTime.getTime() - now.getTime();
                const diffH = Math.round(diffMs / 3600000);
                const timeStr = rainTime.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
                const isToday = rainTime.toLocaleDateString('fi-FI', { timeZone: 'UTC' }) === today;
                const dayLabel = isToday ? 'today' : rainTime.toLocaleDateString('fi-FI', { weekday: 'short', timeZone: 'UTC' });
                rainForecastHtml = `<div class="weather-rain">🌧️ Rain expected ${isToday ? '' : dayLabel + ' '}at ${timeStr} (~${diffH}h) — ${precip[nextRainIndex].toFixed(1)} mm</div>`;
            } else {
                rainForecastHtml = '<div class="weather-rain weather-rain-none">☀️ No rain expected in the next 3 days</div>';
            }

            if (c.weather_code !== 0) {
                let nextSunIndex = -1;
                for (let i = 0; i < times.length; i++) {
                    const forecastTime = new Date(`${times[i]}Z`);
                    if (forecastTime > now && codes[i] === 0) {
                        nextSunIndex = i;
                        break;
                    }
                }
                if (nextSunIndex >= 0) {
                    const sunTime = new Date(`${times[nextSunIndex]}Z`);
                    const diffMs = sunTime.getTime() - now.getTime();
                    const diffH = Math.round(diffMs / 3600000);
                    const timeStr = sunTime.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
                    const isToday = sunTime.toLocaleDateString('fi-FI', { timeZone: 'UTC' }) === today;
                    const dayLabel = isToday ? 'today' : sunTime.toLocaleDateString('fi-FI', { weekday: 'short', timeZone: 'UTC' });
                    sunForecastHtml = `<div class="weather-sun">☀️ Sun expected ${isToday ? '' : dayLabel + ' '}at ${timeStr} (~${diffH}h)</div>`;
                }
            }
        }

        let forecastHtml = '';
        if (data.hourly?.time && data.hourly?.temperature_2m && data.hourly?.weather_code) {
            const times = data.hourly.time;
            const temps = data.hourly.temperature_2m;
            const codes = data.hourly.weather_code;
            const hourMs = 3600000;
            const midnight = new Date(now);
            midnight.setUTCHours(24, 0, 0, 0);
            const forecastHours = times.map((time, index) => ({
                time: new Date(`${time}Z`),
                temperature: temps[index],
                code: codes[index],
            })).filter(hour => hour.time > now && hour.time <= midnight
                && Number.isFinite(hour.temperature) && Number.isFinite(hour.code));
            const selectedHours = forecastHours.filter(hour => hour.time.getTime() <= now.getTime() + 6 * hourMs);
            let lastForecastTime = selectedHours[selectedHours.length - 1]?.time.getTime() ?? now.getTime();
            let gapHours = 2;
            for (const hour of forecastHours) {
                if (hour.time.getTime() >= lastForecastTime + gapHours * hourMs) {
                    selectedHours.push(hour);
                    lastForecastTime = hour.time.getTime();
                    gapHours++;
                }
            }
            const finalHour = forecastHours[forecastHours.length - 1];
            if (finalHour && selectedHours[selectedHours.length - 1] !== finalHour) selectedHours.push(finalHour);
            const snippets = selectedHours.map(hour => {
                const fWeather = getWeatherDescription(hour.code);
                const fTemp = Math.round(hour.temperature);
                const fTimeStr = hour.time.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
                const fDayLabel = hour.time.toLocaleDateString('fi-FI', { timeZone: 'UTC' }) === today
                    ? ''
                    : hour.time.toLocaleDateString('fi-FI', { weekday: 'short', timeZone: 'UTC' }) + ' ';
                return `<div class="weather-forecast-item"><span class="weather-forecast-time">${fDayLabel}${fTimeStr}</span><span class="weather-forecast-icon">${fWeather.icon}</span><span class="weather-forecast-temp">${fTemp}°C</span><span class="weather-forecast-desc">${fWeather.text}</span></div>`;
            });
            if (snippets.length) forecastHtml = `<div class="weather-forecast">${snippets.join('')}</div>`;
        }

        container.innerHTML = `
            <div class="weather-main">
                <span class="weather-temp">${Math.round(c.temperature_2m)}°C</span>
                <span class="weather-condition">
                    <span class="weather-condition-icon">${weather.icon}</span>${weather.text}
                </span>
            </div>
            <div class="weather-details">
                <span><span class="weather-detail-label">Feels like </span><span class="weather-detail-value">${Math.round(c.apparent_temperature)}°C</span></span>
                <span><span class="weather-detail-label">Humidity </span><span class="weather-detail-value">${c.relative_humidity_2m}%</span></span>
                <span><span class="weather-detail-label">Wind </span><span class="weather-detail-value">${c.wind_speed_10m} m/s ${windDir}</span></span>
            </div>
            ${forecastHtml}
            ${rainForecastHtml}
            ${sunForecastHtml}
        `;
        markLoaded(container, 'weather');
    } catch (error) {
        renderError(container, 'weather', 'Could not load weather data.', error);
    }
}
